import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import { Box, Center, Checkbox, HStack } from '@chakra-ui/react';
import { find, forEach, isEmpty, toArray } from 'lodash';
import { BiChevronDown } from 'react-icons/all';
import { useLocation } from 'react-router-dom';
import { components } from 'react-select';

import { defaultCheckBoxStyles, Select } from 'components/DataEntry';
import { FilterComponent } from 'components/FilterView';
import { EnvironmentIcon } from 'components/Icons';
import { getIcon } from 'components/Icons/Components';
import { useNavBarContext } from 'components/Navigation/NavBar/context';
import { selectEnvironmentOrAccountStyles } from 'components/Navigation/NavBar/TopNavBar/RightComponent/SelectEnvironmentOrAccount/styles';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { EnvironmentTypes } from 'containers/App/types';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { useSearchParams } from 'hooks/useSearchParams';

export interface SelectCloudAccountProps {}

export function SelectCloudEnvironmentOrAccount(
  props: SelectCloudAccountProps,
) {
  const {
    user,
    environments,
    accounts: cloudAccountsState,
    onSelectEnvironmentOrAccount,
  } = useNavBarContext();
  const { pathname } = useLocation();
  const { updateValuesInURL, updateValueInURL } = useSearchParamFunctions();
  const params = useSearchParams<{
    environmentId: string;
    accountId: string;
  }>();
  const { accountMapping } = useCloudAccountId();
  const [selectedOption, setSelectedOption] = useState<Record<string, any>>({});
  const isFindingsRoute = pathname.includes('/findings');

  const options = useMemo(() => {
    const items: Record<string, any>[] = [];
    environments.data.forEach((e, envIndex) => {
      items.push({
        label: e?.name,
        icon: <EnvironmentIcon />,
        key: e.id,
        value: {
          type: EnvironmentTypes.Environment,
          id: e.id,
          cloudId: e.name,
          name: e.name,
          cloud: '',
          environmentId: e.id,
          environmentName: e.name,
          environment: e,
        },
      });
      forEach(toArray(e.account_ids) as string[], (a, accountIndex) => {
        const account = accountMapping?.[a];
        const isVisibleOnFindings =
          !isFindingsRoute ||
          e.environment_type !== 'IdentityProvider' ||
          account?.cloud_type === CloudProviders.GoogleWorkspace;

        if (account && isVisibleOnFindings) {
          items.push({
            label: `${account?.name} (${account?.cloud_account_id})`,
            icon: getIcon(account?.cloud_type!),
            value: {
              isAccount: true,
              type: EnvironmentTypes.Account,
              id: account?.uuid,
              cloudId: account?.cloud_account_id,
              name: account?.name,
              cloud: account?.cloud_type,
              environmentId: e.id,
              environmentName: e.name,
              account: { id: account?.uuid, ...account },
            },
            key: `${account?.cloud_account_id}-${e.id ?? '-'}`,
            order: `${envIndex}.${accountIndex}`,
          });
        }
      });
    });
    return items;
  }, [environments.data, accountMapping, isFindingsRoute]);

  const orgId = user.info.org_id;

  //get value from local storage if values are not seen in url
  useEffect(() => {
    if (!params.environmentId || !params.accountId) {
      const envId = localStorage.getItem(orgId + '-environmentId');
      const accountId = localStorage.getItem(orgId + '-accountId');
      updateValuesInURL({
        environmentId: envId,
        accountId: accountId,
      });
      //console.log('localstorage', envId, accountId);
    }
  }, [orgId, updateValuesInURL, params.environmentId, params.accountId]);

  useEffect(() => {
    const selectedOptionIsAvailable = options.some(
      option => option.key === selectedOption?.key,
    );

    if (!selectedOptionIsAvailable && !isEmpty(options)) {
      const persistedOption = params.accountId
        ? find(options, o => o.value?.id === params.accountId)
        : params.environmentId
        ? find(options, o => o.value?.id === params.environmentId)
        : undefined;
      const option = persistedOption ?? options[0];

      setSelectedOption(option);
      onSelectEnvironmentOrAccount(option.value);
    }
  }, [
    options,
    params.accountId,
    params.environmentId,
    selectedOption,
    onSelectEnvironmentOrAccount,
  ]);

  const handleAccountSelection = useCallback(
    data => {
      if (!data) return;

      //set values in locak storage
      localStorage.setItem(
        orgId +
          (data?.value?.type === EnvironmentTypes.Account
            ? '-accountId'
            : '-environmentId'),
        data?.value?.id,
      );
      localStorage.setItem(
        orgId +
          (data?.value?.type === EnvironmentTypes.Environment
            ? '-accountId'
            : '-environmentId'),
        data?.value?.type === EnvironmentTypes.Environment
          ? ''
          : data?.value?.environmentId,
      );

      //update values in url
      if (data?.value?.type === EnvironmentTypes.Environment) {
        updateValuesInURL({
          environmentId: data?.value?.id,
          accountId: '',
        });
      } else {
        updateValueInURL('accountId', data?.value?.id);
        if (params.environmentId !== data?.value?.environmentId) {
          updateValueInURL('environmentId', data?.value?.environmentId);
        }
      }
      setSelectedOption(data);
      onSelectEnvironmentOrAccount(data?.value);
    },
    [
      orgId,
      updateValueInURL,
      onSelectEnvironmentOrAccount,
      params.environmentId,
      updateValuesInURL,
    ],
  );

  const CustomOption = props => {
    const { data } = props;

    const isChecked =
      selectedOption?.value?.type === EnvironmentTypes.Account
        ? selectedOption.key === data.key
        : selectedOption?.value?.environmentId === data.value.environmentId;

    return (
      <components.Option
        {...props}
        isSelected={data.key === selectedOption.key}
      >
        {data.value.isAccount ? (
          <Box pl={3} w="full">
            <Checkbox
              isChecked={isChecked}
              // isDisabled={isDisabled}
              value={data.label}
              alignItems="center"
              {...defaultCheckBoxStyles}
            >
              <HStack spacing={2} align="flex-start">
                <Box boxSize={6} pb={1}>
                  {data?.icon}
                </Box>
                <Box flex={1}>{data.label}</Box>
              </HStack>
            </Checkbox>
          </Box>
        ) : (
          <HStack w="full">
            <Box boxSize={5}>
              <Center>
                <EnvironmentIcon />
              </Center>
            </Box>
            <Box flex={1} fontWeight={600}>
              {data?.label}
            </Box>
          </HStack>
        )}
      </components.Option>
    );
  };

  const DropdownIndicator = props => {
    return (
      <components.DropdownIndicator {...props}>
        <BiChevronDown color="white" size={20} />
      </components.DropdownIndicator>
    );
  };

  const SingleValue = props => {
    const { data } = props;
    return (
      <components.SingleValue {...props}>
        <HStack w="full" minW={0} spacing={2}>
          <Center h="full" boxSize={5} flexShrink={0}>
            <Center w="full" h="full" color="white">
              {data?.icon ?? <EnvironmentIcon color="white" />}
            </Center>
          </Center>
          <Box flex={1} minW={0} overflow="hidden" textOverflow="ellipsis">
            {props.children}
          </Box>
        </HStack>
      </components.SingleValue>
    );
  };

  return (
    <Box className="cloud_accounts">
      <FilterComponent
        excludePaths={[
          'setup',
          'policy-hub',
          'log',
          'compliance',
          'settings',
          'vulnerability/resource',
          'jit',
        ]}
      >
        <FilterComponent filterKey={'select_cloud_account'}>
          <Select
            isLoading={environments.loading || cloudAccountsState.loading}
            onChange={handleAccountSelection}
            options={options}
            value={selectedOption}
            components={{
              Option: CustomOption,
              DropdownIndicator,
              SingleValue,
              ClearIndicator: () => null,
            }}
            styles={selectEnvironmentOrAccountStyles}
            placeholder="Select account"
          />
        </FilterComponent>
      </FilterComponent>
    </Box>
  );
}
